import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import SegmentConfig from '../../../_shared/constants/segmentConfig';
import Detail from '../../../_shared/components/Detail';
import Shipping from '../../../_shared/models/Shipping';
import TransportModal from '../../Modal/TransportModal';
import Loading from '../../../_shared/components/Loading';
import Pill from '../../../_shared/components/Pill';
import ConfirmModal from '../../../_shared/components/ConfirmModal';
import ROUTES from '../../Routes/constants';
import TradeRevDirect from '../../Trd';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties,
  getVinLaunchProperties
} from '../../../_shared/hooks/useAnalytics';

import { StyledInformationIcon } from '../TransportInfo/Styles';
import {
  StyledTransportDetails,
  StyledTransportHeadingContainer,
  StyledTransportHeading,
  StyledTransportCountBadge,
  StyledDetail,
  StyledBox,
  StyledDetailLabel,
  StyledDetailLabelInner,
  StyledDiscAndDateExpected,
  StyledEstimateAndLocation,
  StyledTransportValue,
  StyledPromoOnIndicator,
  StyledPromoOnIndicatorLabel,
  StyledRateInfo,
  StyledDetailValue,
  StyledTransportDriveAwayDisc,
  StyledQuestionIcon
} from './Styles';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';

const propTypes = {
  variant: PropTypes.oneOf(['promo', 'standard']),
  shippingDetails: PropTypes.shape(Shipping),
  dealer: PropTypes.objectOf(PropTypes.any),
  dealerInfo: PropTypes.objectOf(PropTypes.any),
  transportInfo: PropTypes.objectOf(PropTypes.any)
};

const defaultProps = {
  variant: 'standard',
  shippingDetails: undefined,
  dealer: undefined,
  dealerInfo: undefined,
  transportInfo: undefined
};

const TransportDetails = ({
  variant,
  shippingDetails,
  dealer,
  dealerId,
  dealerInfo,
  transportInfo,
  isAtAdesa,
  isFetchingEstimate,
  isMultiOrgAndEnabled,
  isOMP,
  tradeDetails,
  tradeId
}) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [driveAwayModelIsOpen, setDriveAwayModelIsOpen] = useState(false);
  const [driveAwayDisc, setDriveAwayDisc] = useState([]);
  const track = useAnalytics();

  const openModal = () => {
    setIsOpen(true);
    const sourcePrefix = window.location.href.includes(ROUTES.BUYING)
      ? 'mini-'
      : '';

    if (isOMP) {
      track(SegmentConfig.names.vdpCheckTransportInfo, {
        ...getSellerProperties(tradeDetails),
        ...getVinBasicProperties(tradeDetails),
        ...getVinLaunchProperties(tradeDetails)
      });
    } else {
      track(MixpanelConfig.names.transportRateInfo, {
        promo_available: variant === 'promo',
        source: `${sourcePrefix}VDP-transport-details`
      });
    }
  };
  const closeModal = () => {
    setIsOpen(false);
  };

  const openDriveAwayModal = () => {
    setDriveAwayModelIsOpen(true);
    setDriveAwayDisc({
      title: <FormattedMessage id="vdp.transport.NotDriveableDiscTitle" />,
      msg: (
        <FormattedMessage
          id="vdp.transport.NotDriveable"
          values={{
            delimeter: '.'
          }}
        />
      )
    });
  };
  const closeDriveAwayModal = () => {
    setDriveAwayModelIsOpen(false);
    setDriveAwayDisc([]);
  };

  let isOperable = true;
  let hasModifications = false;
  const transportEstimateAvailable =
    shippingDetails &&
    shippingDetails.shippingQuoteFormatted &&
    shippingDetails.distance &&
    dealer &&
    dealer.deliveryAddress &&
    transportInfo;

  const isValidEstimate =
    transportEstimateAvailable &&
    shippingDetails.shippingQuoteFormatted !== 'No quote available' &&
    shippingDetails.distance !== undefined &&
    shippingDetails.distance !== null;

  const dealerPostalCode = dealer?.deliveryAddress?.postalCode?.replace(
    /\s/g,
    ''
  );
  const transportEstimateValue = isValidEstimate
    ? `${convertToCurrency(
        shippingDetails.shippingQuoteFormatted.replace(/\$/g, '')
      )} / ${shippingDetails.distance} `
    : '';
  const transportEstimateAddress = isValidEstimate ? (
    <FormattedMessage
      id="vdp.transport.estimateWithStreet"
      values={{
        street: dealer.deliveryAddress.street,
        city: dealer.deliveryAddress.city,
        province: dealer.deliveryAddress.province,
        postalCode: dealerPostalCode
      }}
    />
  ) : (
    <FormattedMessage id="vdp.transport.NotAvailable" />
  );

  const address = transportInfo.pickupLocation;
  const pickUpPostalCode = address?.postalCode.replace(/\s/g, '');
  const pickUpLocation = address
    ? `${(address.alias ? `${address.alias}, ` : '') + address.street} ${
        address.city
      },
        ${address.province} ${pickUpPostalCode}`
    : '-';
  const { dateExpectedDaysLimit } = transportInfo;

  if (
    transportInfo &&
    transportInfo.isOperableVehicle !== 'N/A' &&
    transportInfo.isModified !== 'N/A'
  ) {
    isOperable = transportInfo.isOperableVehicle === 'Yes';
    hasModifications = transportInfo.isModified === 'Yes';
  }

  return isFetchingEstimate || !transportEstimateAvailable ? (
    <StyledTransportDetails
      id="transport"
      data-testid="vdpTransportDetailsLoading"
    >
      <StyledTransportHeading>
        <Loading width="200px" height="30px" />
      </StyledTransportHeading>
      <StyledBox>
        <StyledDiscAndDateExpected>
          <StyledDetail>
            <Loading width="150px" height="180px" />
          </StyledDetail>
        </StyledDiscAndDateExpected>
        <StyledEstimateAndLocation>
          <StyledDetail>
            <Loading width="150px" height="180px" />
          </StyledDetail>
        </StyledEstimateAndLocation>
      </StyledBox>
    </StyledTransportDetails>
  ) : (
    <StyledTransportDetails id="transport" data-testid="vdpTransportDetails">
      <StyledTransportHeadingContainer id="transportDetailsSection">
        <StyledTransportHeading data-testid="vdpTransportDetails-title">
          <FormattedMessage id="vdp.transport.Details"> </FormattedMessage>
        </StyledTransportHeading>
        {transportInfo?.disclosureCount > 0 && (
          <StyledTransportCountBadge>
            {transportInfo?.disclosureCount}
          </StyledTransportCountBadge>
        )}
      </StyledTransportHeadingContainer>
      <StyledBox>
        <StyledDiscAndDateExpected id="styledDisc">
          <StyledDetail>
            <Detail.Label>
              <FormattedMessage id="vdp.transport.Disclosures" />
            </Detail.Label>
            {isOperable &&
            !hasModifications &&
            !transportInfo.hasDateExpectedExceededLimit ? (
              <Detail.Label className="notranslate">
                <FormattedMessage id="vdp.transport.NothingReported" />
              </Detail.Label>
            ) : (
              <StyledDetailLabel>
                {!isOperable && (
                  <StyledTransportDriveAwayDisc onClick={openDriveAwayModal}>
                    <FormattedMessage
                      id="vdp.transport.NotDriveable"
                      values={{
                        delimeter: ''
                      }}
                    />
                    <StyledQuestionIcon icon="TR5-Question_msg" />
                    <br />
                  </StyledTransportDriveAwayDisc>
                )}
                <ConfirmModal
                  isOpen={driveAwayModelIsOpen}
                  modalLogic={[
                    {
                      msg: driveAwayDisc?.msg,
                      title: driveAwayDisc?.title,
                      onConfirm: () => closeDriveAwayModal,
                      confirmLabel: <FormattedMessage id="modal.ok" />
                    }
                  ]}
                />
                {hasModifications && (
                  <StyledDetailLabelInner>
                    <FormattedMessage id="vdp.transport.IsOversized" />
                  </StyledDetailLabelInner>
                )}
                {transportInfo.hasDateExpectedExceededLimit && (
                  <StyledDetailLabelInner>
                    <FormattedMessage
                      id="vdp.transport.hasDateExpectedExceededLimit"
                      values={{ dateExpectedDaysLimit }}
                    />
                  </StyledDetailLabelInner>
                )}
              </StyledDetailLabel>
            )}
          </StyledDetail>
          <StyledDetail>
            <Detail.Label>
              <FormattedMessage id="vdp.transport.DateExp" />
            </Detail.Label>
            {transportInfo.hasDateExpectedExceededLimit ? (
              <Pill
                testId="dateExpectedLimitWarningTransport"
                variant="warning"
                message={[transportInfo.dateExpected]}
              />
            ) : (
              <Detail.Value className="notranslate">
                {transportInfo.dateExpected}
              </Detail.Value>
            )}
          </StyledDetail>
        </StyledDiscAndDateExpected>
        <StyledEstimateAndLocation>
          <StyledDetail>
            <Detail.Label>
              <FormattedMessage id="vdp.transport.Estimate"> </FormattedMessage>
              {variant === 'promo' && (
                <StyledPromoOnIndicator>
                  <StyledPromoOnIndicatorLabel>
                    <FormattedMessage id="vdp.transport.PromoOn" />
                  </StyledPromoOnIndicatorLabel>
                </StyledPromoOnIndicator>
              )}
            </Detail.Label>
            <TradeRevDirect
              shippingDetails={shippingDetails}
              testId="trd-vdp"
              displayType="vdp"
              dealer={dealerInfo}
              isValidTransportEstimate={isValidEstimate}
              isAtAdesa={isAtAdesa}
            />
            {!isValidEstimate && <Detail.Label>Not available</Detail.Label>}
            {isValidEstimate && (
              <StyledDetailValue className="notranslate">
                <StyledTransportValue>
                  {transportEstimateValue}
                </StyledTransportValue>
                <br />
                {transportEstimateAddress}
              </StyledDetailValue>
            )}
            <StyledRateInfo id="vdpRateInfo" onClick={openModal}>
              <StyledInformationIcon />
              {variant === 'promo' ? (
                <FormattedMessage id="vdp.transport.PromoInfo" />
              ) : (
                <FormattedMessage id="vdp.rateInfo" />
              )}
            </StyledRateInfo>
            <TransportModal
              closeModal={closeModal}
              data={shippingDetails}
              dealerId={dealerId}
              isMultiOrgAndEnabled={isMultiOrgAndEnabled}
              modalIsOpen={modalIsOpen}
              tradeId={tradeId}
            />
          </StyledDetail>
          <StyledDetail>
            <Detail.Label>
              <FormattedMessage id="vdp.transport.VehicleLocation" />
            </Detail.Label>
            <StyledDetailValue className="notranslate">
              {pickUpLocation}
            </StyledDetailValue>
          </StyledDetail>
        </StyledEstimateAndLocation>
      </StyledBox>
    </StyledTransportDetails>
  );
};

TransportDetails.propTypes = propTypes;
TransportDetails.defaultProps = defaultProps;

export default TransportDetails;
